import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #highResImagesLoaded = new Set();
  #hadScrollLock = false;
  #dzState = new WeakMap();
  #scrollerEl = null;
  #boundHandleScroll = null;

  connectedCallback() {
    super.connectedCallback();
    // default scroller is the dialog (carousel mode)
    this.#setScroller(this.refs.dialog);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#setScroller(null);
  }

  #setScroller(el) {
    if (this.#scrollerEl) {
      this.#scrollerEl.removeEventListener('scroll', this.#boundHandleScroll);
      this.#scrollerEl.removeEventListener?.('scrollend', this.#boundHandleScroll);
    }
    this.#scrollerEl = el || null;
    if (!this.#boundHandleScroll) {
      this.#boundHandleScroll = debounce(this.#handleScroll.bind(this), 50);
    }
    if (this.#scrollerEl) {
      this.#scrollerEl.addEventListener('scroll', this.#boundHandleScroll, { passive: true });
      // Where supported, fire a final sync at snap end.
      this.#scrollerEl.addEventListener?.('scrollend', this.#boundHandleScroll);
    }
  }

  #setDragZoomInteractive(enable) {
    const wrappers = this.querySelectorAll('.product-media__drag-zoom-wrapper, .product-media');
    wrappers.forEach((el) => {
      const node = /** @type {HTMLElement} */ (el);
      if (!this.#dzState.has(node)) {
        this.#dzState.set(node, { pointerEvents: node.style.pointerEvents || null, touchAction: node.style.touchAction || null });
      }
      if (enable) {
        const prev = this.#dzState.get(node);
        node.style.pointerEvents = prev?.pointerEvents ?? '';
        node.style.touchAction = prev?.touchAction ?? '';
        node.removeAttribute('data-dz-disabled');
      } else {
        node.style.pointerEvents = 'none';
        node.style.touchAction = '';
        node.setAttribute('data-dz-disabled', '1');
      }
    });
  }

  #applyVerticalPaging(enable) {
    const dialog = this.refs.dialog;
    const gallery = /** @type {HTMLElement|null} */ (this.querySelector('.dialog-zoomed-gallery'));
    if (!dialog || !gallery) return;

    if (enable) {
      if (dialog.hasAttribute('scroll-lock')) {
        this.#hadScrollLock = true;
        dialog.removeAttribute('scroll-lock');
      } else {
        this.#hadScrollLock = false;
      }

      Object.assign(dialog.style, {
        overflowY: 'hidden',
        overflowX: 'hidden',
        touchAction: 'auto',
      });

      Object.assign(gallery.style, {
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollSnapType: 'y mandatory',
        scrollBehavior: 'smooth',
        touchAction: 'pan-y pinch-zoom',
        WebkitOverflowScrolling: 'touch',
      });

      gallery.querySelectorAll('.product-media-container').forEach((li) => {
        const el = /** @type {HTMLElement} */ (li);
        Object.assign(el.style, {
          flex: '0 0 100dvh',
          height: '100dvh',
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          overflow: 'hidden',
        });
      });

      this.#setDragZoomInteractive(false);
      this.#setScroller(gallery);
      this.dataset.verticalPager = '1';
      // run once to sync initial border
      this.#handleScroll();
    } else {
      if (this.#hadScrollLock) dialog.setAttribute('scroll-lock', '');
      dialog.style.overflowY = '';
      dialog.style.overflowX = '';
      dialog.style.touchAction = '';

      gallery.style.display = '';
      gallery.style.flexDirection = '';
      gallery.style.height = '';
      gallery.style.overflowY = '';
      gallery.style.overflowX = '';
      gallery.style.scrollSnapType = '';
      gallery.style.scrollBehavior = '';
      gallery.style.touchAction = '';
      gallery.style.WebkitOverflowScrolling = '';

      gallery.querySelectorAll('.product-media-container').forEach((li) => {
        const el = /** @type {HTMLElement} */ (li);
        el.style.flex = '';
        el.style.height = '';
        el.style.scrollSnapAlign = '';
        el.style.scrollSnapStop = '';
        el.style.overflow = '';
      });

      this.#setDragZoomInteractive(true);
      this.#setScroller(dialog);
      delete this.dataset.verticalPager;
    }
  }

  async open(index, event) {
    event?.preventDefault?.();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();
      if (window.innerWidth < 750) this.#applyVerticalPaging(true);
      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
      // ensure correct thumbnail state right after open
      this.#handleScroll();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetImage) return open();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');
    this.selectThumbnail(index, { behavior: 'instant' });
  }

  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);
    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };
    newImage.src = highResolutionUrl;
  }

  async #handleScroll() {
    const { media, thumbnails } = this.refs;
    const mostVisibleElement = await getMostVisibleElement(media, this.#scrollerEl || undefined);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    // Update the aria-selected state (your CSS uses this for the black border)
    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    // Keep the active thumb centered in view without animation jank
    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: 'auto',
      block: 'center',
      inline: 'center',
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }

  async close() {
    this.#applyVerticalPaging(false);

    if (!supportsViewTransitions) return this.closeDialog();

    const { dialog, media } = this.refs;
    const mostVisibleElement = await getMostVisibleElement(media, this.#scrollerEl || undefined);
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];
    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');
    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);
    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });
    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;
    this.#loadHighResolutionImage(media[index]);
  }

  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];
    if (targetImage) {
      targetImage.scrollIntoView({ behavior: options.behavior });
      this.#loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

function getMostVisibleElement(elements, root) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        const mostVisible = entries.reduce((prev, cur) =>
          cur.intersectionRatio > prev.intersectionRatio ? cur : prev
        );
        obs.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        root: root instanceof Element ? root : null, // use gallery/dialog as viewport
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );
    for (const el of elements) if (el) observer.observe(el);
  });
}
